import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import StructuredData, {
  getBreadcrumbSchema,
  getWebPageSchema,
} from '@/components/feature/StructuredData';
import { articles } from '@/mocks/articles';
import { categories, getCategoryBySlug } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const filteredCategoryArticles = articles.filter((a) => a.categorySlug === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 黑料718 | 最新${category.name}资讯与爆料`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          `黑料718${category.name}频道，每日更新最新${category.name}资讯爆料。${category.description}`
        );
      }
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', `${category.name},黑料718,吃瓜爆料,娱乐资讯`);
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `http://qinglianhb.cn/category/${slug}`);
      }
    }
  }, [category, slug]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const featuredArticles = categoryArticles.slice(0, 3);

  return (
    <>
      <StructuredData
        data={getWebPageSchema(
          `${category.name} - 黑料718`,
          category.description,
          `http://qinglianhb.cn/category/${slug}`
        )}
      />
      <StructuredData
        data={getBreadcrumbSchema([
          { name: '首页', url: 'http://qinglianhb.cn/' },
          { name: category.name, url: `http://qinglianhb.cn/category/${slug}` },
        ])}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Category Hero */}
          <section className="relative pt-16">
            <div className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center justify-center overflow-hidden">
              <img
                src={category.image}
                alt={category.name}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/25 to-background-50"></div>
              <div className="relative z-10 w-full max-w-2xl mx-auto text-center px-4 pt-8 pb-16">
                <nav className="flex items-center justify-center gap-2 text-xs text-background-50/70 mb-4 flex-wrap">
                  <Link to="/" className="hover:text-background-50 transition-colors whitespace-nowrap">
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-background-50 whitespace-nowrap">{category.name}</span>
                </nav>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight">
                  {category.name}
                </h1>
                <p className="mt-3 text-sm text-background-50/80 max-w-md mx-auto leading-relaxed">
                  {category.description}
                </p>
                <div className="inline-flex items-center gap-2 mt-5 px-4 py-1.5 bg-background-50/15 rounded-full">
                  <i className="ri-article-line text-background-50/80 text-sm"></i>
                  <span className="text-xs text-background-50/80">共 {category.count} 条爆料</span>
                </div>
              </div>
            </div>

            {/* Featured Articles */}
            <div className="relative -mt-16 px-4 md:px-6 pb-8">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {featuredArticles.map((article, idx) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className={`group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer ${
                        idx === 1 ? 'md:-mt-6' : 'md:mt-0'
                      }`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-md whitespace-nowrap">
                          {idx === 0 ? '最新爆料' : '热门'}
                        </span>
                      </div>
                      <div className="p-4">
                        <h2 className="text-sm font-heading font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h2>
                        <div className="flex items-center justify-between mt-3 text-xs text-foreground-400">
                          <span>{article.publishDate}</span>
                          <span>{article.author}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* All Articles Grid */}
          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 md:mb-8">
                全部{category.name}资讯
              </h2>
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-500">暂无相关爆料，请稍后再来</p>
                </div>
              )}
            </div>
          </section>

          {/* Category Intro */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                <div className="w-full lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      title={`${category.name} - 黑料718频道`}
                      className="w-full aspect-[4/3] object-cover object-top"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                    {category.name}
                  </span>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 leading-snug">
                    关于{category.name}频道
                  </h2>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    {category.description}
                    我们拥有一支专业的编辑团队，24小时不间断监控各大社交媒体和新闻源头，确保您第一时间获取最热、最新、最全的{category.name}资讯。
                  </p>
                  <div className="flex items-center gap-6 mt-5">
                    <div>
                      <p className="text-lg font-bold text-foreground-950">{category.count.toLocaleString()}</p>
                      <p className="text-xs text-foreground-400">累计爆料</p>
                    </div>
                    <div className="w-px h-10 bg-background-300"></div>
                    <div>
                      <p className="text-lg font-bold text-foreground-950">24h</p>
                      <p className="text-xs text-foreground-400">全天更新</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Other Categories */}
          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 md:mb-8">
                其他频道
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="group flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-lg group-hover:bg-primary-100 group-hover:text-primary-500 transition-colors">
                        <i className={cat.icon}></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.name}</p>
                        <p className="text-xs text-foreground-400">{cat.count} 条</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Person%20scrolling%20news%20feed%20on%20smartphone%20in%20cozy%20environment%2C%20warm%20lighting%2C%20relaxed%20atmosphere%2C%20modern%20lifestyle%2C%20editorial%20photography%2C%20soft%20textures&width=1200&height=500&seq=cta-category-banner&orientation=landscape"
                  alt="下载黑料718 APP"
                  className="w-full h-48 md:h-64 object-cover object-top"
                />
              </div>
              <div className="text-center">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
                  不错过任何一条<span className="italic text-primary-500 font-normal">爆料</span>
                </h2>
                <p className="mt-3 text-sm text-foreground-600 max-w-md mx-auto">
                  下载黑料718 APP，开启推送通知，<strong>第一时间</strong>获取{category.name}最新资讯。
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-2-line"></i>
                  立即下载 APP
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}